import { Container, Graphics, Sprite, Text, Rectangle, type Texture, type FederatedPointerEvent } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, DESIGN_H, BORDER, SCRIM } from '../app/theme';
import { viewport } from '../app/viewport';
import { Panel, Button, makeText, sceneBackground, avatarSprite, iconSprite } from '../ui/widgets';
import { handFan, handRow, CARD_ASPECT } from '../ui/cardSprite';
import { ChatControl } from '../ui/chatControl';
import { scaleIn, pulse, flyTo, fadeTo, animate, tweenProps, easeOutCubic, linear, type TweenHandle } from '../app/anim';
import { turnRing, glowRing } from '../ui/effects';
import {
  didHokmDeckAppendCard,
  hokmDealCardSfxVolume,
  initSound,
  playSfx,
  isSfxMuted,
  setSfxMuted,
} from '../app/sound';
import { loadTableTextures, type TableTextures } from '../assets/tableAssets';
import type { AppContext } from '../app/AppContext';
import type { Atlas } from '../assets/AtlasLoader';
import { MessageTag } from '../net/messageTags';
import { Hokm, Role, RoomName } from '../net/enums';
import { type GameInfoSeat, encodeExitView, encodeReadyToPlay, encodeResignGame } from '../net/codec/matchmaking';
import { encodeRefresh, decodeShowEndGameResult, decodeDisconnect } from '../net/codec/blackjack';
import { decodeChat, encodeChat } from '../net/codec/chat';
import {
  encodeSelectHokm,
  encodeSelectCard,
  decodeHokmGetGame,
  decodeSelectHokm,
  decodeSelectHakem,
  decodeInGameMessage,
  hokmGlyph,
  hokmIsRed,
  hokmLabelFa,
  hokmOptions,
} from '../net/codec/hokm';
import type { HokmGamePayload, SelectHokmPayload, SelectHakemPayload } from '../net/models/hokmGame';
import { toPersianDigits } from '../util/persian';
import { computeHokmLayout, HOKM_HAND_MAX_WIDTH, HOKM_SCORE_PILL_Y, type HokmLayout } from './hokmLayout';
import { RESIGN_RESPONSE_TIMEOUT_MS, ResignLifecycle } from './resignLifecycle';

const WIN_GREEN = 0x4ade80;
const ORANGE = 0xd9722e;
const TOP_BAR_H = 112;

// Top-zone player pods + centered info pills — identical anchors to BlackjackTableScene (same frame).
const POD_W = 280;
const POD_H = 96;
const POD_Y = 8;
const POD_MARGIN = 8;
const POD_AVATAR = 64;
const POD_AV_X = 10;
const POD_AV_Y = 14;
const TURN_RING_R = POD_AVATAR / 2 + 7; // 39
const SCORE_PILL_Y = HOKM_SCORE_PILL_Y;
const AV_CY = POD_Y + POD_AV_Y + POD_AVATAR / 2; // 54
const ME_AV_CX = POD_MARGIN + POD_AV_X + POD_AVATAR / 2; // 50
const OPP_AV_CX = DESIGN_W - POD_MARGIN - POD_W + (POD_W - POD_AV_X - POD_AVATAR) + POD_AVATAR / 2; // 670

type Phase = 'waiting' | 'hakemReveal' | 'trumpPick' | 'playing';

/**
 * The Hokm table — a 2-player trick-taking game on the same frame as Blackjack (wood backdrop, top bar
 * with player pods + scores, timer ring, chat, and resign menu). Only the card system differs: a
 * 13-card hand + 3 face-down sub-packs per player, a center trick of up to 4 cards, a hakem who picks
 * the trump (hokm), and drag-to-play onto a highlighted center drop-box. The server is the single
 * source of truth: this scene renders the latest packet and sends SelectHokm / SelectCard.
 */
export class HokmTableScene extends Scene {
  private cards: Atlas | null = null;
  private table: TableTextures = {};
  private chatEnabled = true;

  private phase: Phase = 'waiting';
  private game: HokmGamePayload | null = null; // last GetGame / GamePreview
  private hokmPick: SelectHokmPayload | null = null; // last SelectHokm
  private hakemReveal: SelectHakemPayload | null = null; // last SelectHakem
  private awaitingState = false; // suppress input after a send until the next server packet
  private readonly resignFlow = new ResignLifecycle();
  private preserveResignFeedback = false;
  private readonly isPreview: boolean;

  private readonly myRole: Role;
  private readonly bgLayer = new Container();
  private readonly feltLayer = new Container();
  private readonly chrome = new Container();
  private readonly board = new Container();
  private readonly fx = new Container();
  private readonly overlay = new Container();
  private chat!: ChatControl;

  private ly: HokmLayout = computeHokmLayout(DESIGN_H);

  // Drag-to-play bookkeeping.
  private dragCard: Container | null = null;
  private dragCode = '';
  private dragHome = { x: 0, y: 0, rot: 0 };
  private dragGrab = { x: 0, y: 0 };
  private dropZoneG: Graphics | null = null;
  private dropHot = false;

  private prevMyLen = 0;
  private prevOppLen = 0;
  private prevDeck: string[] = [];
  private prevP1Tricks = 0;
  private prevP2Tricks = 0;
  private audibleDeck: string[] | null = null;
  private readonly hakemDealSoundedIndices = new Set<number>();
  private timerHandle: TweenHandle | null = null;

  constructor(
    private readonly ctx: AppContext,
    private readonly seat: GameInfoSeat,
  ) {
    super();
    this.myRole = seat.myRole;
    this.isPreview = seat.isPreview;
  }

  onEnter(): void {
    this.addChild(this.bgLayer, this.feltLayer, this.chrome, this.board, this.fx, this.overlay);
    this.rebuildBg();

    this.chat = new ChatControl({
      respackFrame: (n) => this.ctx.respackFrame(n),
      onSend: (text) => this.sendChat(text),
      mySide: { x: 150, y: 96 },
      oppSide: { x: DESIGN_W - 150, y: 96 },
    });
    this.fx.addChild(this.chat);

    initSound();
    this.buildChrome();

    this.track(this.ctx.client.on(MessageTag.GetGame, (r) => this.onGetGame(decodeHokmGetGame(r))));
    this.track(this.ctx.client.on(MessageTag.GamePreview, (r) => this.onGetGame(decodeHokmGetGame(r))));
    this.track(this.ctx.client.on(MessageTag.SelectHokm, (r) => this.onSelectHokm(decodeSelectHokm(r))));
    this.track(this.ctx.client.on(MessageTag.SelectHakem, (r) => this.onSelectHakem(decodeSelectHakem(r))));
    this.track(this.ctx.client.on(MessageTag.InGameMessage, (r) => this.onInGameMessage(decodeInGameMessage(r))));
    this.track(this.ctx.client.on(MessageTag.ShowEndGameResult, (r) => this.onEndGame(decodeShowEndGameResult(r))));
    this.track(this.ctx.client.on(MessageTag.Disconnect, (r) => this.onDisconnect(decodeDisconnect(r))));
    this.track(this.ctx.client.on(MessageTag.Chat, (r) => this.onChat(decodeChat(r))));

    void import('../assets/cardAtlas')
      .then(({ loadCardAtlas }) => loadCardAtlas())
      .then((atlas) => {
        this.cards = atlas;
        this.render();
      })
      .catch((err: unknown) => console.warn('[hokm] card atlas failed to load', err));

    void loadTableTextures()
      .then((tex) => {
        this.table = tex;
        this.buildFelt();
        this.buildChrome();
        this.render();
      })
      .catch((err: unknown) => console.warn('[hokm] table art failed to load', err));

    this.ctx.client.send(encodeReadyToPlay());
    this.render();
  }

  onResize(): void {
    this.rebuildBg();
    this.buildFelt();
    this.buildChrome();
    this.render();
  }

  private rebuildBg(): void {
    for (const ch of this.bgLayer.removeChildren()) ch.destroy({ children: true });
    this.bgLayer.addChild(sceneBackground());
  }

  private computeLayout(): void {
    this.ly = computeHokmLayout(viewport.h);
  }

  // ---- static chrome ----

  private buildFelt(): void {
    // The felt art (public/img/table.png) — reused from Blackjack; cover-fit below the top bar.
    this.feltLayer.removeChildren().forEach((c) => c.destroy());
    const tex = this.table.table;
    if (!tex) return;
    const s = new Sprite(tex);
    s.anchor.set(0.5, 0);
    s.scale.set(Math.max(DESIGN_W / tex.width, (viewport.h - TOP_BAR_H) / tex.height));
    s.position.set(DESIGN_W / 2, TOP_BAR_H);
    this.feltLayer.addChild(s);
  }

  private buildChrome(): void {
    this.computeLayout();
    for (const c of this.chrome.removeChildren()) c.destroy({ children: true });

    const bar = new Graphics().rect(0, 0, DESIGN_W, TOP_BAR_H).fill({ color: 0x0a0a0c, alpha: 0.9 });
    this.chrome.addChild(bar);

    const logoTex = this.ctx.iconTexture('hokm');
    if (logoTex) {
      const logo = iconSprite(logoTex, 48);
      logo.position.set(DESIGN_W / 2 - 24, 5);
      this.chrome.addChild(logo);
    }

    const menu = new Button(this.isPreview ? 'بازگشت' : 'منوی بازی', () => this.menuAction(), {
      width: 120,
      height: 38,
      fontSize: 18,
      icon: this.isPreview ? (this.ctx.iconTexture('return') ?? null) : null,
    });
    menu.position.set(DESIGN_W / 2 - 60, 66);
    this.chrome.addChild(menu);

    const refresh = this.railButton('', () => this.ctx.client.send(encodeRefresh()), this.ctx.iconTexture('refresh-page-option'));
    refresh.position.set(DESIGN_W - 76, this.ly.railRefreshY);
    this.chrome.addChild(refresh);
    const chatBtn = this.railButton('گپ', () => this.toggleChat(), this.ctx.respackFrame('chat'));
    chatBtn.position.set(DESIGN_W - 76, this.ly.railChatY);
    this.chrome.addChild(chatBtn);
  }

  private railButton(label: string, onTap: () => void, tex?: Texture): Container {
    const c = new Container();
    const w = 64;
    const h = 64;
    const g = new Graphics().roundRect(0, 0, w, h, 12).fill({ color: ORANGE });
    c.addChild(g);
    if (tex) {
      const icon = iconSprite(tex, label ? 34 : 44);
      icon.position.set((w - (label ? 34 : 44)) / 2, label ? 6 : 10);
      c.addChild(icon);
    }
    if (label) {
      const t = makeText(label, { size: 20, weight: '700', align: 'center', fill: COLORS.white });
      t.anchor.set(0.5);
      t.position.set(w / 2, tex ? h - 16 : h / 2);
      c.addChild(t);
    }
    c.hitArea = new Rectangle(0, 0, w, h);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    c.on('pointerover', () => (c.alpha = 0.85));
    c.on('pointerout', () => (c.alpha = 1));
    return c;
  }

  // ---- incoming messages ----

  private onGetGame(g: HokmGamePayload): void {
    if (this.resignFlow.isTerminal) return;
    const preserveOverlay = this.consumeResignFeedback();
    const cardWasPlayed = didHokmDeckAppendCard(this.audibleDeck, g.deck);
    this.audibleDeck = g.deck.slice();
    this.phase = 'playing';
    this.game = g;
    this.hokmPick = null;
    this.hakemReveal = null;
    this.awaitingState = false;
    if (!preserveOverlay) this.clearOverlay();
    this.render();
    if (cardWasPlayed) playSfx('hokm-card');
  }

  private onSelectHokm(p: SelectHokmPayload): void {
    if (this.resignFlow.isTerminal) return;
    const preserveOverlay = this.consumeResignFeedback();
    this.audibleDeck = null;
    this.hakemDealSoundedIndices.clear();
    this.phase = 'trumpPick';
    this.hokmPick = p;
    this.hakemReveal = null;
    this.awaitingState = false;
    if (!preserveOverlay) this.clearOverlay();
    this.render();
  }

  private onSelectHakem(p: SelectHakemPayload): void {
    if (this.resignFlow.isTerminal) return;
    this.audibleDeck = null;
    this.hakemDealSoundedIndices.clear();
    this.phase = 'hakemReveal';
    this.hakemReveal = p;
    this.awaitingState = false;
    this.render();
  }

  private onInGameMessage(text: string): void {
    if (this.resignFlow.isTerminal) return;
    if (this.resignFlow.acknowledge()) {
      this.clearOverlay();
      this.preserveResignFeedback = Boolean(text);
    }
    if (text) this.banner(text, COLORS.gold);
    // A rejected SelectCard ("نوبت شما نیست" etc.) lands here — re-enable the hand.
    this.awaitingState = false;
    this.render();
  }

  private onEndGame(eg: ReturnType<typeof decodeShowEndGameResult>): void {
    if (!this.resignFlow.complete()) return;
    this.showEndGame(eg.resultText, eg.reasonText);
  }

  private onDisconnect(d: ReturnType<typeof decodeDisconnect>): void {
    if (this.resignFlow.isTerminal) return;
    this.banner(d.isLeaving ? 'حریف از بازی خارج شد' : 'حریف به بازی بازگشت', d.isLeaving ? COLORS.danger : COLORS.gold);
  }

  private onChat(payload: ReturnType<typeof decodeChat>): void {
    if (this.resignFlow.isTerminal || !this.chatEnabled) return;
    const side = payload.senderRole === this.myRole ? 'me' : 'opp';
    this.chat.showBubble(side, payload.text);
  }

  // ---- actions ----

  private sendSelectCard(code: string): void {
    this.ctx.client.send(encodeSelectCard(code));
    this.awaitingState = true;
    this.render();
  }

  private sendSelectHokm(h: Hokm): void {
    this.ctx.client.send(encodeSelectHokm(h));
    this.awaitingState = true;
    this.render();
  }

  private sendChat(text: string): void {
    this.ctx.client.send(encodeChat(text));
    this.chat.showBubble('me', text);
  }

  private toggleChat(): void {
    if (this.chatEnabled) this.chat.toggle();
    else this.banner('چت غیرفعال است', COLORS.textDim);
  }

  /** Spectators leave the watch list; seated players open the regular game menu. */
  private menuAction(): void {
    if (!this.isPreview) {
      this.openMenu();
      return;
    }
    this.ctx.client.send(encodeExitView());
    this.ctx.navigate(RoomName.MainMenu);
  }

  private openMenu(): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.55 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.clearOverlay());
    layer.addChild(dim);

    const boxW = 520;
    const boxH = 330;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('منوی بازی', { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 26);
    panel.addChild(title);

    const resign = new Button('اعلام باخت', () => this.confirmResign(), { width: boxW - 80, height: 64, fill: COLORS.danger });
    resign.position.set(40, 84);
    panel.addChild(resign);

    const halfW = (boxW - 80 - 20) / 2;
    const chatToggle = new Button(
      this.chatEnabled ? 'خاموش کردن چت' : 'روشن کردن چت',
      () => {
        this.chatEnabled = !this.chatEnabled;
        this.clearOverlay();
      },
      { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text },
    );
    chatToggle.position.set(40, 164);
    panel.addChild(chatToggle);

    const soundToggle = new Button(
      isSfxMuted() ? 'روشن کردن صدا' : 'خاموش کردن صدا',
      () => {
        setSfxMuted(!isSfxMuted());
        this.clearOverlay();
      },
      { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text },
    );
    soundToggle.position.set(40 + halfW + 20, 164);
    panel.addChild(soundToggle);

    const close = new Button('بستن', () => this.clearOverlay(), { width: boxW - 80, height: 64 });
    close.position.set(40, 244);
    panel.addChild(close);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private confirmResign(): void {
    this.confirmDialog('اعلام باخت', 'با اعلام باخت، این دست را واگذار می‌کنید و حریف برنده می‌شود.', 'بله، باخت را می‌پذیرم', () => this.resignGame());
  }

  private confirmDialog(titleText: string, message: string, confirmLabel: string, onConfirm: () => void): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: SCRIM.dialog });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.openMenu());
    layer.addChild(dim);

    const boxW = 560;
    const boxH = 320;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    panel.eventMode = 'static';
    panel.hitArea = new Rectangle(0, 0, boxW, boxH);
    layer.addChild(panel);

    const title = makeText(titleText, { size: 30, weight: '700', align: 'center', fill: COLORS.danger });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 30);
    panel.addChild(title);

    const msg = makeText(message, { size: 22, align: 'center', fill: COLORS.text });
    msg.anchor.set(0.5, 0);
    msg.style.wordWrap = true;
    msg.style.wordWrapWidth = boxW - 80;
    msg.position.set(boxW / 2, 102);
    panel.addChild(msg);

    const btnW = (boxW - 100) / 2;
    const yes = new Button(confirmLabel, onConfirm, { width: btnW, height: 64, fontSize: 20, fill: COLORS.danger });
    yes.position.set(40, boxH - 92);
    panel.addChild(yes);
    const no = new Button('بازگشت', () => this.openMenu(), { width: btnW, height: 64, fill: COLORS.panelSoft, textColor: COLORS.text });
    no.position.set(40 + btnW + 20, boxH - 92);
    panel.addChild(no);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private showEndGame(resultText: string, reasonText: string): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, DESIGN_W, viewport.h).fill({ color: COLORS.scrim, alpha: 0.7 });
    dim.eventMode = 'static';
    layer.addChild(dim);

    const boxW = 600;
    const boxH = 420;
    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('پایان بازی', { size: 32, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 28);
    panel.addChild(title);

    const result = makeText(resultText, { size: 26, weight: '700', align: 'center', fill: COLORS.white });
    result.style.wordWrap = true;
    result.style.wordWrapWidth = boxW - 64;
    result.anchor.set(0.5, 0);
    result.position.set(boxW / 2, 110);
    panel.addChild(result);

    if (reasonText) {
      const reason = makeText(reasonText, { size: 22, align: 'center', fill: COLORS.textDim });
      reason.style.wordWrap = true;
      reason.style.wordWrapWidth = boxW - 64;
      reason.anchor.set(0.5, 0);
      reason.position.set(boxW / 2, 200);
      panel.addChild(reason);
    }

    const back = new Button('بازگشت به منو اصلی', () => this.ctx.navigate(RoomName.MainMenu), { width: boxW - 80, height: 72, fontSize: 26 });
    back.position.set(40, boxH - 96);
    panel.addChild(back);

    this.overlay.addChild(layer);
    scaleIn(panel, 260, 0.8);
  }

  private resignGame(): void {
    if (!this.resignFlow.begin()) return;

    this.awaitingState = true;
    this.clearOverlay();
    this.banner('در انتظار پاسخ سرور…', COLORS.gold, RESIGN_RESPONSE_TIMEOUT_MS);
    this.render();
    try {
      this.ctx.client.send(encodeResignGame());
    } catch {
      this.resignFlow.acknowledge();
      this.awaitingState = false;
      this.clearOverlay();
      this.banner('درخواست ارسال نشد؛ اتصال به سرور در دسترس نیست.', COLORS.danger);
      this.render();
      return;
    }

    this.after(RESIGN_RESPONSE_TIMEOUT_MS, () => {
      if (!this.resignFlow.acknowledge()) return;
      this.awaitingState = false;
      this.clearOverlay();
      this.banner('پاسخی از سرور دریافت نشد؛ دوباره تلاش کنید.', COLORS.danger);
      this.render();
    });
  }

  private consumeResignFeedback(): boolean {
    const preserve = this.preserveResignFeedback;
    this.preserveResignFeedback = false;
    return preserve;
  }

  // ---- rendering ----

  private render(): void {
    this.computeLayout();
    this.timerHandle?.cancel();
    this.timerHandle = null;
    // A pending drag is invalidated by a fresh board; drop it before destroying the holder.
    this.dragCard = null;
    this.dropHot = false;
    this.dropZoneG = null;
    for (const child of this.board.removeChildren()) child.destroy({ children: true });

    if (this.phase === 'playing' && this.game) {
      this.renderPlaying(this.game);
    } else if (this.phase === 'trumpPick' && this.hokmPick) {
      this.renderTrumpPick(this.hokmPick);
    } else if (this.phase === 'hakemReveal' && this.hakemReveal) {
      this.renderHakemReveal(this.hakemReveal);
    } else {
      this.board.addChild(this.centered('در انتظار شروع بازی…', viewport.h / 2, { size: 26, fill: COLORS.gold }));
    }
  }

  /** The shared board frame (pods, hakem crown, trump indicator, score/trick pill, turn ring). */
  private renderFrame(fd: {
    hakem: Role;
    turn: Role;
    hokm: Hokm;
    p1Tricks: number;
    p2Tricks: number;
    p1Hands: number;
    p2Hands: number;
    p1Bank: number;
    p2Bank: number;
    nextTrigger: number;
  }): void {
    const myIsP1 = this.myRole === Role.Player1;
    const myAvatar = myIsP1 ? this.seat.player1.avatar : this.seat.player2.avatar;
    const oppAvatar = myIsP1 ? this.seat.player2.avatar : this.seat.player1.avatar;
    const myName = myIsP1 ? this.seat.player1.username : this.seat.player2.username;
    const oppName = myIsP1 ? this.seat.player2.username : this.seat.player1.username;
    const myBank = myIsP1 ? fd.p1Bank : fd.p2Bank;
    const oppBank = myIsP1 ? fd.p2Bank : fd.p1Bank;
    // دست (hand) = PlayerWin 0–7, first to 7 wins the round; راند (round) = PlayerTotalWin, to GameCount.
    const myHandWins = myIsP1 ? fd.p1Tricks : fd.p2Tricks;
    const oppHandWins = myIsP1 ? fd.p2Tricks : fd.p1Tricks;
    const myRoundWins = myIsP1 ? fd.p1Hands : fd.p2Hands;
    const oppRoundWins = myIsP1 ? fd.p2Hands : fd.p1Hands;
    const totalRounds = this.seat.game.gameCount;

    // The score lives INSIDE each pod (the only top zone the opponent's 13-card fan never covers).
    this.board.addChild(this.playerPod('me', { avatar: myAvatar, name: myName, timeBank: myBank, roundWins: myRoundWins, handWins: myHandWins, totalRounds }));
    this.board.addChild(this.playerPod('opp', { avatar: oppAvatar, name: oppName, timeBank: oppBank, roundWins: oppRoundWins, handWins: oppHandWins, totalRounds }));

    // The «دست» count (tricks won this round, 0–7) as a face-down pile in the empty felt just below
    // each avatar — the won cards "collect" here (see runTrickCollect). Mirrors the Unity table.
    const mePile = this.wonTricksPile('me', myHandWins);
    mePile.position.set(ME_AV_CX, SCORE_PILL_Y + 50);
    this.board.addChild(mePile);
    const oppPile = this.wonTricksPile('opp', oppHandWins);
    oppPile.position.set(OPP_AV_CX, SCORE_PILL_Y + 50);
    this.board.addChild(oppPile);

    // Hakem crown over the ruler's avatar.
    if (fd.hakem !== Role.None) {
      this.board.addChild(this.crown(fd.hakem === this.myRole ? 'me' : 'opp'));
    }

    // Trump indicator (prominent, just under the pods).
    this.board.addChild(this.trumpIndicator(fd.hokm));

    // Active-turn timer ring on the active player's avatar.
    if (fd.turn !== Role.None) {
      const myTurn = fd.turn === this.myRole;
      this.timerHandle = this.turnTimer(myTurn ? ME_AV_CX : OPP_AV_CX, fd.nextTrigger, myTurn ? myBank : oppBank);
    }
  }

  private renderPlaying(g: HokmGamePayload): void {
    // A trick just completed when the previous packet held the full 4-card trick, this packet has
    // cleared the deck, and exactly one trick counter rose by +1 (the server's two-packet flow:
    // Packet A = 4-card deck, Packet B ~2s later = empty deck + winner's PlayerWin+1). Infer the
    // winner from which counter rose. Captured BEFORE the prev* snapshot is committed below.
    const trickWon =
      this.cards != null &&
      this.prevDeck.length === 4 &&
      g.deck.length < 4 &&
      g.player1Tricks + g.player2Tricks === this.prevP1Tricks + this.prevP2Tricks + 1;
    let sweepSide: 'me' | 'opp' | null = null;
    let sweepCodes: string[] | null = null;
    if (trickWon) {
      const winnerRole = g.player1Tricks === this.prevP1Tricks + 1 ? Role.Player1 : Role.Player2;
      sweepSide = winnerRole === this.myRole ? 'me' : 'opp';
      sweepCodes = this.prevDeck.slice();
    }

    this.renderFrame({
      hakem: g.hakem,
      turn: g.turn,
      hokm: g.hokm,
      p1Tricks: g.player1Tricks,
      p2Tricks: g.player2Tricks,
      p1Hands: g.player1HandsWon,
      p2Hands: g.player2HandsWon,
      p1Bank: g.player1TimeBank,
      p2Bank: g.player2TimeBank,
      nextTrigger: g.turnNextTrigger,
    });

    // Opponent face-down fan (top) + their 3 sub-packs.
    this.oppHandLayer(g.oppHand, this.ly.oppHandY);
    this.subRow([g.oppSub1, g.oppSub2, g.oppSub3], this.ly.oppSubY);
    this.prevOppLen = g.oppHand.length;

    // Center: the drop-zone box (only when I can play) then the current trick.
    const myTurn = g.turn === this.myRole;
    const canPlay = myTurn && !this.awaitingState && !this.isPreview && g.available.length > 0;
    this.board.addChild(this.trickDeck(g.deck));

    // My 3 sub-packs then my hand (hand added last so a lifted card draws above everything).
    // Once 2–3 cards sit in the trick, the server's `available` holds each sub-pack's TOP card
    // (not hand cards), so the same set makes the right sub tops draggable and dims the hand.
    this.subRow([g.mySub1, g.mySub2, g.mySub3], this.ly.mySubY, new Set(canPlay ? g.available : []));
    this.myHandLayer(g.myHand, new Set(canPlay ? g.available : []), this.ly.myHandY);
    this.prevMyLen = g.myHand.length;

    if (!myTurn && !this.isPreview) {
      this.board.addChild(this.centered(this.awaitingState ? 'در حال پردازش…' : 'نوبت حریف…', this.ly.myHandY - 70, { size: 22, fill: COLORS.textDim }));
    }

    // Commit this packet's snapshot, then (board + pile now built) sweep the just-won trick into
    // the winner's pile. Resetting prevDeck to the cleared deck blocks a replay on a same-`g`
    // re-render (InGameMessage / resize), and the strict `=== +1` guard ignores new-hand resets.
    this.prevDeck = g.deck.slice();
    this.prevP1Tricks = g.player1Tricks;
    this.prevP2Tricks = g.player2Tricks;
    if (sweepSide && sweepCodes) this.runTrickCollect(sweepSide, sweepCodes);
  }

  private renderTrumpPick(p: SelectHokmPayload): void {
    this.renderFrame({
      hakem: p.hakem,
      turn: p.turn,
      hokm: Hokm.NotSet,
      p1Tricks: p.player1Tricks,
      p2Tricks: p.player2Tricks,
      p1Hands: p.player1HandsWon,
      p2Hands: p.player2HandsWon,
      p1Bank: p.player1TimeBank,
      p2Bank: p.player2TimeBank,
      nextTrigger: p.turnNextTrigger,
    });

    // My first-5 cards (display only — the full hand arrives with GetGame after the pick).
    this.myHandLayer(p.myHand, new Set(), this.ly.myHandY);

    const iAmHakem = p.hakem === this.myRole;
    if (iAmHakem && !this.awaitingState && !this.isPreview) {
      this.board.addChild(this.trumpPicker(hokmOptions(p.hokmType)));
    } else {
      this.board.addChild(this.centered(iAmHakem ? 'در حال پردازش…' : 'در انتظار انتخاب حکم توسط حاکم…', this.ly.deckCY, { size: 24, fill: COLORS.gold }));
    }
  }

  private renderHakemReveal(p: SelectHakemPayload): void {
    // Draw the frame WITHOUT the crown — the deal reveals who gets the Ace (the hakem), so don't spoil it.
    this.renderFrame({
      hakem: Role.None,
      turn: Role.None,
      hokm: Hokm.NotSet,
      p1Tricks: 0,
      p2Tricks: 0,
      p1Hands: 0,
      p2Hands: 0,
      p1Bank: 0,
      p2Bank: 0,
      nextTrigger: 0,
    });
    this.board.addChild(this.centered('تعیین حاکم…', this.ly.deckCY - 170, { size: 24, weight: '700', fill: COLORS.gold }));
    this.runHakemDeal(p);
  }

  /**
   * Hakem reveal: deal the dealt cards out one-by-one, alternating between the two sides — exactly how the
   * server decides the hakem. Both players get random cards and the single Ace is dealt LAST; whoever
   * receives it is the ruler. When the Ace lands, its side lights up (card glow + crown + name). The driver
   * binds a fresh board child, so it self-cancels the instant the SelectHokm re-render (~5s) tears the board down.
   */
  private runHakemDeal(p: SelectHakemPayload): void {
    if (p.hakem === Role.None) return;
    const p1 = p.player1Hand ? p.player1Hand.split('-').filter(Boolean) : [];
    const p2 = p.player2Hand ? p.player2Hand.split('-').filter(Boolean) : [];
    const meCards = (this.myRole === Role.Player1 ? p1 : p2).slice();
    const oppCards = (this.myRole === Role.Player1 ? p2 : p1).slice();
    if (!meCards.length && !oppCards.length) return;

    // The Ace sits last (highest StackOrder) in the hakem's hand — pull it out to deal it last & alone.
    const aceOwner: 'me' | 'opp' = p.hakem === this.myRole ? 'me' : 'opp';
    const ownerCards = aceOwner === 'me' ? meCards : oppCards;
    let aceIdx = ownerCards.findIndex((c) => c[0] === 'A');
    if (aceIdx < 0) aceIdx = ownerCards.length - 1; // fallback: treat the last card as the decider
    const aceCode = ownerCards[aceIdx] ?? 'AS';
    const meRest = meCards.filter((_, i) => !(aceOwner === 'me' && i === aceIdx));
    const oppRest = oppCards.filter((_, i) => !(aceOwner === 'opp' && i === aceIdx));

    // Deal order: alternate opp/me non-Ace cards, then the Ace last to its owner.
    const seq: { side: 'me' | 'opp'; code: string; isAce: boolean }[] = [];
    const rounds = Math.max(meRest.length, oppRest.length);
    for (let k = 0; k < rounds; k++) {
      if (k < oppRest.length) seq.push({ side: 'opp', code: oppRest[k], isAce: false });
      if (k < meRest.length) seq.push({ side: 'me', code: meRest[k], isAce: false });
    }
    seq.push({ side: aceOwner, code: aceCode, isAce: true });

    const n = seq.length;
    const cw = this.ly.subW;
    const ch = cw * CARD_ASPECT;
    const step = Math.max(12, Math.round(cw * 0.42));
    const meTotal = meCards.length;
    const oppTotal = oppCards.length;
    const meRowY = this.ly.deckCY + 56;
    const oppRowY = this.ly.deckCY - 56 - ch;
    const slot = { me: 0, opp: 0 };
    let aceCard: Container | null = null;

    const dealOne = (i: number): void => {
      const s = seq[i];
      const idx = slot[s.side]++;
      const total = s.side === 'me' ? meTotal : oppTotal;
      const targetX = DESIGN_W / 2 - ((total - 1) * step) / 2 + idx * step;
      const targetY = s.side === 'me' ? meRowY : oppRowY;
      const card = handFan(this.cards, [s.code], { cardWidth: cw, shadow: true });
      card.position.set(DESIGN_W / 2, this.ly.deckCY); // fly out from the center
      this.board.addChild(card);
      flyTo(card, targetX, targetY, 220);
      scaleIn(card, 200, 0.5);
      const volume = hokmDealCardSfxVolume(this.hakemDealSoundedIndices, i, s.isAce);
      if (volume !== null) playSfx('hokm-card', volume);
      if (s.isAce) aceCard = card;
    };

    const reveal = (): void => {
      if (this.phase !== 'hakemReveal') return;
      const hakemMe = aceOwner === 'me';
      if (aceCard && !aceCard.destroyed) {
        pulse(aceCard, 1.45, 420);
        aceCard.addChild(new Graphics().roundRect(-cw / 2 - 4, -4, cw + 8, ch + 8, 8).stroke({ width: 4, color: COLORS.gold, alpha: 0.95 }));
      }
      glowRing(this.board, hakemMe ? ME_AV_CX : OPP_AV_CX, AV_CY, TURN_RING_R + 6, COLORS.gold);
      const crown = this.crown(hakemMe ? 'me' : 'opp');
      this.board.addChild(crown);
      scaleIn(crown, 320, 0.2);
      const name = p.hakem === Role.Player1 ? this.seat.player1.username : this.seat.player2.username;
      const label = this.centered(`${name} حاکم شد`, this.ly.deckCY, { size: 30, weight: '700', fill: COLORS.gold });
      label.alpha = 0;
      this.board.addChild(label);
      fadeTo(label, 1, 300);
    };

    const fxAnchor = new Container();
    this.board.addChild(fxAnchor);
    const dealMs = Math.min(3600, n * 110);
    let dealt = 0;
    animate({
      duration: dealMs,
      ease: linear,
      bind: fxAnchor,
      onUpdate: (t) => {
        const target = Math.min(n, Math.floor(t * n) + 1);
        while (dealt < target) dealOne(dealt++);
      },
      onComplete: reveal,
    });
  }

  /**
   * On a completed trick, sweep its 4 face-up cards from the deck center into the winner's won-tricks
   * pile (below their avatar): each card turns landscape and shrinks-and-fades as it lands. The pile count
   * itself is authoritative (already rendered at the server's incremented value) — this is pure flourish.
   * Spawned on `this.fx` (which survives a board rebuild) so an InGameMessage/resize mid-flight can't
   * wipe the cards; the bound fxAnchor self-cancels every tween if the scene tears down.
   */
  private runTrickCollect(side: 'me' | 'opp', codes: string[]): void {
    if (!this.cards || !codes.length) return;
    const targetX = side === 'me' ? ME_AV_CX : OPP_AV_CX;
    const targetY = SCORE_PILL_Y + 50;
    const w = this.ly.trickW;
    const targetScale = this.ly.wonW / w;
    const landingRotation = side === 'me' ? -Math.PI / 2 : Math.PI / 2;
    const step = w * 0.75; // matches trickDeck's handRow(..., 0.25) spacing
    const startX = DESIGN_W / 2 - ((codes.length - 1) * step) / 2;

    const fxAnchor = new Container();
    this.fx.addChild(fxAnchor);

    codes.forEach((code, i) => {
      const card = handFan(this.cards, [code], { cardWidth: w, shadow: true });
      const cx = startX + i * step;
      card.position.set(cx, this.ly.deckCY);
      fxAnchor.addChild(card);
      animate({
        duration: 420,
        delay: i * 40,
        ease: easeOutCubic,
        bind: fxAnchor,
        onUpdate: (t) => {
          if (card.destroyed) return;
          card.position.set(cx + (targetX - cx) * t, this.ly.deckCY + (targetY - this.ly.deckCY) * t);
          card.rotation = t * landingRotation;
          card.scale.set(1 - (1 - targetScale) * t);
          card.alpha = t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1;
        },
      });
    });

    this.after(420 + (codes.length - 1) * 40 + 60, () => {
      if (!fxAnchor.destroyed) fxAnchor.destroy({ children: true });
    });
  }

  // ---- board pieces ----

  private playerPod(
    side: 'me' | 'opp',
    d: { avatar: number; name: string; timeBank: number; roundWins: number; handWins: number; totalRounds: number },
  ): Container {
    const pod = new Panel(POD_W, POD_H, { fill: COLORS.panel, alpha: 0.92, shadow: true });
    pod.position.set(side === 'me' ? POD_MARGIN : DESIGN_W - POD_MARGIN - POD_W, POD_Y);

    const avX = side === 'me' ? POD_AV_X : POD_W - POD_AV_X - POD_AVATAR;
    const av = avatarSprite(this.ctx.profileAtlas, d.avatar, POD_AVATAR);
    av.position.set(avX, POD_AV_Y);
    pod.addChild(av);

    // راند = rounds won (the match score, to totalRounds). The «دست» count (tricks this round) is no
    // longer text here — it renders as a face-down pile below the avatar (see wonTricksPile).
    const roundStr = d.totalRounds > 0 ? `${toPersianDigits(d.roundWins)}/${toPersianDigits(d.totalRounds)}` : `${toPersianDigits(d.roundWins)}`;
    const scoreStr = `راند ${roundStr}`;

    if (side === 'me') {
      const tx = POD_AV_X + POD_AVATAR + 12;
      pod.addChild(this.leftText(d.name, tx, 10, 20, COLORS.text, '700'));
      pod.addChild(this.leftText(scoreStr, tx, 36, 15, COLORS.gold, '700'));
      pod.addChild(this.timeChip(d.timeBank, tx, 58, 'me'));
    } else {
      const tx = POD_W - POD_AV_X - POD_AVATAR - 12;
      pod.addChild(this.rightTextAt(d.name, tx, 10, 20, COLORS.text, '700'));
      pod.addChild(this.rightTextAt(scoreStr, tx, 36, 15, COLORS.gold, '700'));
      pod.addChild(this.timeChip(d.timeBank, tx, 58, 'opp'));
    }
    return pod;
  }

  private timeChip(seconds: number, anchorX: number, y: number, side: 'me' | 'opp'): Container {
    const c = new Container();
    const padX = 10;
    const h = 30;
    const iconSize = 22;
    const icon = iconSprite(this.ctx.iconTexture('clock'), iconSize);
    const num = makeText(`${toPersianDigits(seconds)}″`, { size: 19, weight: '700', fill: COLORS.text, align: 'left' });
    num.anchor.set(0, 0.5);
    const chipW = padX + iconSize + 6 + Math.ceil(num.width) + padX;
    const bg = new Graphics()
      .roundRect(0, 0, chipW, h, h / 2)
      .fill({ color: COLORS.darkPill, alpha: 0.85 })
      .stroke({ width: BORDER.thin, color: COLORS.panelBorder, alpha: 0.6 });
    icon.position.set(padX, (h - iconSize) / 2);
    num.position.set(padX + iconSize + 6, h / 2);
    c.addChild(bg, icon, num);
    c.position.set(side === 'me' ? anchorX : anchorX - chipW, y);
    return c;
  }

  /** A small gold crown drawn over the hakem's avatar. */
  private crown(side: 'me' | 'opp'): Container {
    const c = new Container();
    const w = 36;
    const h = 24;
    const g = new Graphics();
    g.moveTo(0, h)
      .lineTo(0, 5)
      .lineTo(w * 0.25, h * 0.55)
      .lineTo(w * 0.5, 0)
      .lineTo(w * 0.75, h * 0.55)
      .lineTo(w, 5)
      .lineTo(w, h)
      .closePath()
      .fill({ color: COLORS.gold })
      .stroke({ width: 1.5, color: 0x6b4e12 });
    c.addChild(g);
    const cx = side === 'me' ? ME_AV_CX : OPP_AV_CX;
    c.position.set(cx - w / 2, POD_Y - 8);
    return c;
  }

  /** The «حکم: <suit>» indicator — a colored pip for standard suits, a Persian word for Naras/Saras. */
  private trumpIndicator(hokm: Hokm): Container {
    const c = new Container();
    const label = makeText('حکم:', { size: 22, weight: '700', fill: COLORS.textDim, align: 'left' });
    label.anchor.set(0, 0.5);
    const glyph = hokmGlyph(hokm);
    const value =
      glyph !== null
        ? makeText(glyph, { size: 30, weight: '700', fill: hokmIsRed(hokm) ? COLORS.danger : COLORS.white, align: 'left' })
        : makeText(hokmLabelFa(hokm), { size: 22, weight: '700', fill: hokm === Hokm.NotSet ? COLORS.textDim : COLORS.gold, align: 'left' });
    value.anchor.set(0, 0.5);

    const padX = 16;
    const gap = 10;
    const h = 44;
    const w = padX + Math.ceil(label.width) + gap + Math.ceil(value.width) + padX;
    const bg = new Graphics().roundRect(0, 0, w, h, h / 2).fill({ color: COLORS.darkPill, alpha: 0.92 }).stroke({ width: BORDER.base, color: COLORS.panelBorder });
    label.position.set(padX, h / 2);
    value.position.set(padX + Math.ceil(label.width) + gap, h / 2);
    c.addChild(bg, label, value);
    c.position.set((DESIGN_W - w) / 2, SCORE_PILL_Y);
    return c;
  }

  private oppHandLayer(codes: string[], y: number): void {
    if (!codes.length) return;
    const row = handFan(this.cards, codes, { cardWidth: this.ly.oppCardW, spread: 0.16, shadow: true, maxWidth: HOKM_HAND_MAX_WIDTH });
    row.position.set(DESIGN_W / 2, y);
    this.board.addChild(row);
    const start = codes.length > this.prevOppLen ? this.prevOppLen : 0;
    row.children.forEach((card, i) => {
      if (i >= start) scaleIn(card as Container, 260, 0.4);
    });
  }

  private myHandLayer(codes: string[], available: Set<string>, y: number): void {
    if (!codes.length) return;
    const w = this.ly.cardW;
    const h = w * CARD_ASPECT;
    const row = handFan(this.cards, codes, { cardWidth: w, spread: 0.16, shadow: true, maxWidth: HOKM_HAND_MAX_WIDTH });
    row.position.set(DESIGN_W / 2, y);
    this.board.addChild(row);

    row.children.forEach((child, i) => {
      const holder = child as Container;
      const code = codes[i];
      if (available.has(code)) {
        // Playable: gold outline + drag-to-play.
        const outline = new Graphics().roundRect(0, 0, w, h, Math.max(4, w * 0.08)).stroke({ width: 3, color: COLORS.gold, alpha: 0.95 });
        holder.addChild(outline);
        const homeX = row.position.x + holder.position.x;
        const homeY = row.position.y + holder.position.y;
        this.makeDraggable(holder, code, homeX, homeY, holder.rotation);
      } else if (!available.size) {
        // Nothing playable right now (off-turn / awaiting): leave at full alpha, just not interactive.
      } else {
        // My turn but this card is not a legal play: dim it.
        holder.alpha = 0.45;
      }
    });

    const start = codes.length > this.prevMyLen ? this.prevMyLen : codes.length < this.prevMyLen ? 0 : codes.length;
    row.children.forEach((card, i) => {
      if (i >= start) scaleIn(card as Container, 280, 0.3);
    });
  }

  /** A row of 3 sub-pack piles, centered. Pass `draggable` (the local player's) to play their tops. */
  private subRow(packs: string[][], y: number, draggable?: Set<string>): void {
    const w = this.ly.subW;
    const spacing = w * 2.1;
    const center = DESIGN_W / 2;
    const xs = [center - spacing, center, center + spacing];
    packs.forEach((codes, i) => {
      const pile = this.subPile(codes, xs[i], y, w, draggable);
      if (pile) this.board.addChild(pile);
    });
  }

  /** One sub-pack: a small stack with the (possibly revealed) top card flush on top + a count chip. */
  private subPile(codes: string[], cx: number, cy: number, w: number, draggable?: Set<string>): Container | null {
    const n = codes.length;
    if (!n) return null;
    const c = new Container();
    const h = w * CARD_ASPECT;
    // Draw deepest first so codes[0] (the revealed top, if any) ends on top at offset 0.
    let topCard: Container | null = null;
    for (let i = n - 1; i >= 0; i--) {
      const card = handFan(this.cards, [codes[i]], { cardWidth: w, shadow: false });
      card.position.set(cx + i * 2.5, cy + i * 2.5);
      c.addChild(card);
      if (i === 0) topCard = card;
    }
    const count = makeText(toPersianDigits(n), { size: 15, weight: '700', fill: COLORS.white, align: 'center' });
    count.anchor.set(0.5);
    const chip = new Graphics().circle(cx + w / 2 + 4, cy + h - 6, 11).fill({ color: COLORS.darkPill, alpha: 0.92 }).stroke({ width: BORDER.thin, color: COLORS.panelBorder });
    count.position.set(cx + w / 2 + 4, cy + h - 6);
    c.addChild(chip, count);

    // Only the TOP card is ever playable. The sub-piles are spaced apart, so they play on a TAP (no
    // mis-touch risk, unlike the tightly-fanned hand). handFan pivots a single card at bottom-center, so
    // its sprite spans the fan-container's local (-w/2, 0)..(w/2, h).
    if (topCard && draggable?.has(codes[0])) {
      const outline = new Graphics().roundRect(-w / 2, 0, w, h, Math.max(4, w * 0.08)).stroke({ width: 3, color: COLORS.gold, alpha: 0.95 });
      topCard.addChild(outline);
      this.makeTappable(topCard, codes[0], w);
    }
    return c;
  }

  /**
   * A player's won-tricks pile (the «دست» count, 0–7): landscape face-down cards overlapping
   * strictly horizontally toward the table center, with a prominent count chip on the inward edge.
   */
  private wonTricksPile(side: 'me' | 'opp', count: number): Container {
    const c = new Container();
    const n = Math.max(0, Math.min(7, count));
    if (!n || !this.cards) return c;
    const w = this.ly.wonW;
    const h = w * CARD_ASPECT;
    const direction = side === 'me' ? 1 : -1;
    const step = Math.max(9, Math.round(w * 0.14));
    const rotation = side === 'me' ? -Math.PI / 2 : Math.PI / 2;
    // Draw deepest-first so the front card sits on top.
    for (let i = n - 1; i >= 0; i--) {
      const card = handFan(this.cards, ['BB'], { cardWidth: w, shadow: false });
      card.position.set(i * step * direction, 0);
      card.rotation = rotation;
      c.addChild(card);
    }
    const chipCx = direction * ((n - 1) * step + h - 8);
    const chipCy = 0;
    const chip = new Graphics()
      .circle(chipCx, chipCy, 14)
      .fill({ color: COLORS.darkPill, alpha: 0.96 })
      .stroke({ width: BORDER.thin, color: COLORS.panelBorder });
    const num = makeText(toPersianDigits(n), {
      size: 18,
      weight: '700',
      fill: COLORS.white,
      align: 'center',
    });
    num.anchor.set(0.5);
    num.position.set(chipCx, chipCy);
    c.addChild(chip, num);
    return c;
  }

  /** The current trick (0–4 face-up cards), centered on the deck point. */
  private trickDeck(codes: string[]): Container {
    const c = new Container();
    if (!codes.length) return c;
    const w = this.ly.trickW;
    const h = w * CARD_ASPECT;
    const row = handRow(this.cards, codes, w, 0.25);
    const rw = row.getLocalBounds().width;
    row.position.set(DESIGN_W / 2 - rw / 2, this.ly.deckCY - h / 2);
    c.addChild(row);
    return c;
  }

  /** The trump picker for the hakem: a panel of suit (and Naras/Saras) buttons. */
  private trumpPicker(options: Hokm[]): Container {
    const cols = options.length > 4 ? 4 : options.length;
    const rows = Math.ceil(options.length / cols);
    const cell = 120;
    const gap = 16;
    const padX = 24;
    const padTop = 64;
    const padBottom = 20;
    const boxW = padX * 2 + cols * cell + (cols - 1) * gap;
    const boxH = padTop + rows * cell + (rows - 1) * gap + padBottom;

    const panel = new Panel(boxW, boxH);
    panel.position.set((DESIGN_W - boxW) / 2, this.ly.deckCY - boxH / 2);

    const title = makeText('حکم را انتخاب کنید', { size: 24, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 20);
    panel.addChild(title);

    options.forEach((opt, i) => {
      const col = i % cols;
      const rowI = Math.floor(i / cols);
      const x = padX + col * (cell + gap);
      const y = padTop + rowI * (cell + gap);
      panel.addChild(this.trumpButton(opt, x, y, cell));
    });

    scaleIn(panel, 220, 0.85);
    return panel;
  }

  private trumpButton(opt: Hokm, x: number, y: number, size: number): Container {
    const c = new Container();
    c.position.set(x, y);
    const bg = new Graphics().roundRect(0, 0, size, size, 16).fill({ color: COLORS.darkPill }).stroke({ width: BORDER.base, color: COLORS.gold });
    c.addChild(bg);

    const glyph = hokmGlyph(opt);
    if (glyph !== null) {
      const g = makeText(glyph, { size: 56, weight: '700', align: 'center', fill: hokmIsRed(opt) ? COLORS.danger : COLORS.white });
      g.anchor.set(0.5);
      g.position.set(size / 2, size / 2 - 10);
      c.addChild(g);
    }
    const lbl = makeText(hokmLabelFa(opt), { size: glyph !== null ? 18 : 24, weight: '700', align: 'center', fill: COLORS.text });
    lbl.anchor.set(0.5);
    lbl.position.set(size / 2, glyph !== null ? size - 22 : size / 2);
    c.addChild(lbl);

    c.hitArea = new Rectangle(0, 0, size, size);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', () => this.sendSelectHokm(opt));
    c.on('pointerover', () => (c.alpha = 0.85));
    c.on('pointerout', () => (c.alpha = 1));
    return c;
  }

  // ---- drag-to-play ----

  private makeDraggable(holder: Container, code: string, homeX: number, homeY: number, homeRot: number): void {
    const w = this.ly.cardW;
    const h = w * CARD_ASPECT;
    holder.eventMode = 'static';
    holder.cursor = 'grab';
    holder.hitArea = new Rectangle(0, 0, w, h);
    holder.on('pointerdown', (e: FederatedPointerEvent) => this.onDragStart(e, holder, code, homeX, homeY, homeRot));
    holder.on('globalpointermove', (e: FederatedPointerEvent) => this.onDragMove(e));
    holder.on('pointerup', (e: FederatedPointerEvent) => this.onDragEnd(e));
    holder.on('pointerupoutside', (e: FederatedPointerEvent) => this.onDragEnd(e));
  }

  /** Sub-pack tops play on a single TAP — the 3 piles are spaced out, so there's no mis-touch risk. */
  private makeTappable(holder: Container, code: string, cardW: number): void {
    const h = cardW * CARD_ASPECT;
    holder.eventMode = 'static';
    holder.cursor = 'pointer';
    holder.hitArea = new Rectangle(-cardW / 2, 0, cardW, h);
    holder.on('pointertap', () => {
      const g = this.game;
      if (this.awaitingState || this.isPreview || !g || g.turn !== this.myRole || !g.available.includes(code)) return;
      this.board.addChild(holder); // lift above so it reads as played, then fly to the trick
      flyTo(holder, DESIGN_W / 2, this.ly.deckCY, 160);
      this.sendSelectCard(code);
    });
  }

  private onDragStart(e: FederatedPointerEvent, holder: Container, code: string, homeX: number, homeY: number, homeRot: number): void {
    if (this.dragCard) return;
    this.dragCard = holder;
    this.dragCode = code;
    this.dragHome = { x: homeX, y: homeY, rot: homeRot };
    this.showDropZone();
    // Lift to the board's top so it draws above all and uses board coords.
    this.board.addChild(holder);
    holder.position.set(homeX, homeY);
    holder.rotation = 0;
    const p = e.getLocalPosition(this.board);
    this.dragGrab = { x: p.x - holder.x, y: p.y - holder.y };
    pulse(holder, 1.08, 140);
  }

  private onDragMove(e: FederatedPointerEvent): void {
    const holder = this.dragCard;
    if (!holder) return;
    if (holder.destroyed) {
      this.dragCard = null;
      this.hideDropZone();
      return;
    }
    const p = e.getLocalPosition(this.board);
    holder.position.set(p.x - this.dragGrab.x, p.y - this.dragGrab.y);
    const hot = this.isOverDropZone(e.global);
    if (hot !== this.dropHot) {
      this.dropHot = hot;
      this.paintDropZone(hot);
    }
  }

  private onDragEnd(e: FederatedPointerEvent): void {
    const holder = this.dragCard;
    if (!holder) return;
    this.dragCard = null;
    if (holder.destroyed) {
      this.hideDropZone();
      return;
    }

    const g = this.game;
    const playable = this.isOverDropZone(e.global) && !!g && g.turn === this.myRole && !this.awaitingState && g.available.includes(this.dragCode);
    this.dropHot = false;
    this.hideDropZone();
    if (playable) {
      flyTo(holder, DESIGN_W / 2, this.ly.deckCY, 160);
      this.sendSelectCard(this.dragCode); // render() then replaces the board with the server's next state
    } else {
      // Snap home, then rebuild the board so the card drops back to its proper sibling z-order —
      // onDragStart lifted it to the top of `board`, which would otherwise cover its right-hand neighbours.
      holder.rotation = this.dragHome.rot;
      tweenProps(
        holder.position as unknown as Record<string, number>,
        { x: this.dragHome.x, y: this.dragHome.y },
        { duration: 200, ease: easeOutCubic, bind: holder, onComplete: () => this.render() },
      );
    }
  }

  private isOverDropZone(global: { x: number; y: number }): boolean {
    const lp = this.board.toLocal(global);
    const z = this.ly.dropZone;
    return lp.x >= z.x && lp.x <= z.x + z.w && lp.y >= z.y && lp.y <= z.y + z.h;
  }

  private buildDropZone(hot: boolean): Graphics {
    const z = this.ly.dropZone;
    const g = new Graphics().roundRect(z.x, z.y, z.w, z.h, 24);
    if (hot) g.fill({ color: COLORS.accent, alpha: 0.18 }).stroke({ width: 4, color: COLORS.accent });
    else g.fill({ color: COLORS.scrim, alpha: 0.12 }).stroke({ width: 2, color: COLORS.panelBorder, alpha: 0.6 });
    return g;
  }

  private showDropZone(): void {
    this.hideDropZone();
    this.dropZoneG = this.buildDropZone(false);
    this.board.addChild(this.dropZoneG);
  }

  private hideDropZone(): void {
    const g = this.dropZoneG;
    this.dropZoneG = null;
    if (!g || g.destroyed) return;
    g.parent?.removeChild(g);
    g.destroy();
  }

  private paintDropZone(hot: boolean): void {
    const g = this.dropZoneG;
    if (!g || g.destroyed) return;
    const z = this.ly.dropZone;
    g.clear().roundRect(z.x, z.y, z.w, z.h, 24);
    if (hot) g.fill({ color: COLORS.accent, alpha: 0.18 }).stroke({ width: 4, color: COLORS.accent });
    else g.fill({ color: COLORS.scrim, alpha: 0.12 }).stroke({ width: 2, color: COLORS.panelBorder, alpha: 0.6 });
  }

  // ---- turn timer (copied from the Blackjack table — same VFX) ----

  private turnTimer(cx: number, turnSeconds: number, reserveSeconds: number): TweenHandle | null {
    if (turnSeconds <= 0 && reserveSeconds <= 0) return null;

    const nr = 19;
    const node = new Container();
    const bg = new Graphics();
    const t = makeText('', { size: 20, weight: '700', align: 'center', fill: COLORS.white });
    t.anchor.set(0.5);
    node.addChild(bg, t);
    node.position.set(cx, AV_CY + TURN_RING_R);
    this.board.addChild(node);

    const paintNode = (color: number): void => {
      bg.clear();
      bg.circle(0, 0, nr + 1)
        .fill({ color: COLORS.scrim, alpha: 0.5 })
        .circle(0, 0, nr)
        .fill({ color: COLORS.darkPill, alpha: 1 })
        .stroke({ width: BORDER.thick, color });
    };

    let active: TweenHandle | null = null;
    let lastSec = -1;

    const startReserve = (): void => {
      paintNode(COLORS.danger);
      t.style.fill = COLORS.danger;
      lastSec = -1;
      pulse(node, 1.3, 320);
      active = turnRing(this.board, {
        cx,
        cy: AV_CY,
        radius: TURN_RING_R,
        seconds: reserveSeconds,
        color: COLORS.danger,
        width: 7,
        onTick: (s) => {
          if (t.destroyed) return;
          t.text = toPersianDigits(s);
          if (s !== lastSec) {
            lastSec = s;
            pulse(node, 1.14, 280);
          }
        },
      });
      this.board.addChild(node);
    };

    if (turnSeconds > 0) {
      paintNode(WIN_GREEN);
      active = turnRing(this.board, {
        cx,
        cy: AV_CY,
        radius: TURN_RING_R,
        seconds: turnSeconds,
        color: WIN_GREEN,
        width: 7,
        onTick: (s) => {
          if (!t.destroyed) t.text = toPersianDigits(s);
        },
        onComplete: () => {
          active?.cancel();
          active = null;
          if (reserveSeconds > 0) startReserve();
        },
      });
      this.board.addChild(node);
    } else {
      startReserve();
    }

    return {
      cancel() {
        active?.cancel();
        if (!node.destroyed) node.destroy();
      },
    };
  }

  // ---- small text helpers ----

  private centered(text: string, y: number, opts: { size?: number; weight?: '400' | '700'; fill?: number } = {}): Container {
    const t = makeText(text, { size: opts.size ?? 24, weight: opts.weight ?? '400', align: 'center', fill: opts.fill ?? COLORS.text });
    t.anchor.set(0.5);
    t.position.set(DESIGN_W / 2, y);
    const c = new Container();
    c.addChild(t);
    return c;
  }

  private leftText(text: string, x: number, y: number, size: number, fill: number, weight: '400' | '700' = '400'): Text {
    const t = makeText(text, { size, weight, fill, align: 'left' });
    t.anchor.set(0, 0);
    t.position.set(x, y);
    return t;
  }

  private rightTextAt(text: string, x: number, y: number, size: number, fill: number, weight: '400' | '700' = '400'): Text {
    const t = makeText(text, { size, weight, fill });
    t.anchor.set(1, 0);
    t.position.set(x, y);
    return t;
  }

  // ---- transient overlays ----

  private banner(text: string, color: number, durationMs = 3000): void {
    const c = new Container();
    const w = DESIGN_W - 120;
    const h = 60;
    const panel = new Panel(w, h);
    panel.position.set((DESIGN_W - w) / 2, 470);
    const t = makeText(text, { size: 22, align: 'center', fill: color });
    t.anchor.set(0.5);
    t.style.wordWrap = true;
    t.style.wordWrapWidth = w - 40;
    t.position.set(w / 2, h / 2);
    panel.addChild(t);
    c.addChild(panel);
    this.overlay.addChild(c);
    scaleIn(panel, 200, 0.85);
    this.after(durationMs, () => {
      if (!c.destroyed) c.destroy({ children: true });
    });
  }

  private clearOverlay(): void {
    for (const child of this.overlay.removeChildren()) child.destroy({ children: true });
  }

  private after(ms: number, fn: () => void): void {
    const id = window.setTimeout(fn, ms);
    this.track(() => window.clearTimeout(id));
  }
}
